import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getWithTokenAPI } from "../../util/Api";
import { batch_get_all, students_list_autoapproved, students_list_approved } from "../../util/Apiendpoits";
import { SafeToast } from "../Toast/ShowToast";
import "../../styles/batchdetails.css";
import {
  FiArrowLeft,
  FiBookOpen,
  FiUsers,
  FiDollarSign,
  FiFileText,
  FiCheckCircle,
  FiClock,
  FiEye,
  FiExternalLink,
  FiSearch,
} from "react-icons/fi";
import TableSkeleton from "../ReusableComponent/TableSkeleton";

const BatchDetails = () => {
  const { batchId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, userId } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState("tests"); // "tests" | "students"
  const [loading, setLoading] = useState(true);
  const [batchInfo, setBatchInfo] = useState(location.state?.batch || null);
  const [tests, setTests] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [testSearch, setTestSearch] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Fetch all tests and enrolled students for this batch
  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      const instId = userId || "695f3f832929fa82230aaf33";

      // 1. Fetch batch information if not present from state
      let currentBatch = batchInfo;
      if (!currentBatch || !currentBatch.testBatchName) {
        const batchRes = await getWithTokenAPI(
          token,
          `${batch_get_all}${instId}&all=true`
        );
        if (batchRes?.statusCode === 200) {
          const allBatches = batchRes.data.data || [];
          currentBatch = allBatches.find((b) => b._id === batchId || b.id === batchId);
          if (currentBatch) {
            setBatchInfo(currentBatch);
          }
        }
      }

      const batchName = currentBatch?.testBatchName;

      // 2. Fetch all tests associated with this batch name
      if (batchName) {
        const testsRes = await getWithTokenAPI(
          token,
          `/batch/get-all?Institution_id=${instId}&testBatchName=${encodeURIComponent(batchName)}&all=true`
        );

        if (testsRes?.statusCode === 200) {
          const rawTests = testsRes.data?.data || [];
          // Deduplicate by testNo and sort
          const uniqueTests = [
            ...new Map(rawTests.map((t) => [t.testNo, t])).values(),
          ].sort((a, b) => (Number(a.testNo) || 0) - (Number(b.testNo) || 0));

          setTests(uniqueTests);
        }
      }

      // 3. Fetch all students & payment subscriptions for this batch
      // Query both auto-approved (full payments) and approved/pending platform subscriptions
      const autoQuery = `${students_list_autoapproved}&institutionId=${instId}&limit=1000${
        batchName ? `&testBatchName=${encodeURIComponent(batchName)}` : `&batchId=${batchId}`
      }`;
      const approvedQuery = `${students_list_approved}&institutionId=${instId}&limit=1000${
        batchName ? `&testBatchName=${encodeURIComponent(batchName)}` : `&batchId=${batchId}`
      }`;
      const pendingQuery = `/admin/user-subscriptions?isApproved=false&paymentType=PLATFORM_ONLY&institutionId=${instId}&limit=1000${
        batchName ? `&testBatchName=${encodeURIComponent(batchName)}` : `&batchId=${batchId}`
      }`;

      const [resAuto, resApproved, resPending] = await Promise.all([
        getWithTokenAPI(token, autoQuery),
        getWithTokenAPI(token, approvedQuery),
        getWithTokenAPI(token, pendingQuery),
      ]);

      const listAuto = Array.isArray(resAuto?.data) ? resAuto.data : [];
      const listApproved = Array.isArray(resApproved?.data) ? resApproved.data : [];
      const listPending = Array.isArray(resPending?.data) ? resPending.data : [];

      const combined = [...listAuto, ...listApproved, ...listPending];

      // Format enrolled students
      const formatted = combined.map((sub) => {
        const studentFee = Number(sub.testPrice) || Number(currentBatch?.testFees) || 0;
        const isPaid = sub.paymentStatus === "paid" || sub.paymentType === "FULL" || sub.isApproved;
        const paidAmount = isPaid ? studentFee : 0;
        const dueBalance = isPaid ? 0 : studentFee;

        return {
          id: sub._id,
          studentId: sub.userId?._id,
          name: sub.userId?.name || sub.name || "N/A",
          phone: sub.userId?.phoneNumber || sub.phoneNumer || "N/A",
          city: sub.userId?.city || "N/A",
          state: sub.userId?.state || "",
          instituteId: sub.institutionId?._id || instId,
          batchId: sub.batchId?._id || batchId,
          totalFee: studentFee,
          paidAmount: paidAmount,
          dueBalance: dueBalance,
          paymentStatus: sub.paymentStatus || (isPaid ? "paid" : "pending"),
          paymentMethod: sub.paymentMethod || (sub.paymentType === "FULL" ? "RAZORPAY" : "OFFLINE / UPI"),
          transactionId: sub.transactionId || "N/A",
          receiptUrl: sub.upload_img || null,
          enrolledDate: sub.createdAt
            ? new Date(sub.createdAt).toLocaleDateString("en-GB")
            : "N/A",
          isApproved: sub.isApproved,
        };
      });

      setSubscriptions(formatted);
    } catch (err) {
      console.error("Error fetching batch details:", err);
      SafeToast.error("Failed to load batch details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchDetails();
  }, [batchId]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const totalFeesPerStudent = Number(batchInfo?.testFees) || 0;
    const totalStudents = subscriptions.length;
    const totalCollected = subscriptions.reduce((acc, curr) => acc + curr.paidAmount, 0);
    const totalDue = subscriptions.reduce((acc, curr) => acc + curr.dueBalance, 0);
    const publishedTestsCount = tests.filter((t) => t.isPublished).length;

    return {
      totalFeesPerStudent,
      totalStudents,
      totalCollected,
      totalDue,
      totalTests: tests.length || batchInfo?.numberofTests || 0,
      publishedTestsCount,
    };
  }, [batchInfo, subscriptions, tests]);

  // Filtered Tests
  const filteredTests = useMemo(() => {
    if (!testSearch.trim()) return tests;
    return tests.filter(
      (t) =>
        t.testBatchName?.toLowerCase().includes(testSearch.toLowerCase()) ||
        String(t.testNo).includes(testSearch) ||
        t.subTopic?.toLowerCase().includes(testSearch.toLowerCase())
    );
  }, [tests, testSearch]);

  // Filtered Students
  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return subscriptions;
    return subscriptions.filter(
      (s) =>
        s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
        s.phone.includes(studentSearch) ||
        s.paymentStatus.toLowerCase().includes(studentSearch.toLowerCase()) ||
        s.transactionId.toLowerCase().includes(studentSearch.toLowerCase())
    );
  }, [subscriptions, studentSearch]);

  return (
    <div className="batch-details-page">
      {/* Header Bar */}
      <div className="batch-details-header">
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button className="batch-back-btn" onClick={() => navigate("/batches")}>
            <FiArrowLeft size={16} />
            Back to Batches
          </button>
          <div>
            <h2 style={{ margin: 0, color: "#0f172a", fontSize: "22px", fontWeight: 700 }}>
              {batchInfo?.testBatchName || "Batch Details"}
            </h2>
            <span style={{ fontSize: "13px", color: "#64748b" }}>
              Exam Type: <strong>{batchInfo?.examType?.value || "N/A"}</strong>
              {batchInfo?.headlineTagline ? ` • ${batchInfo.headlineTagline}` : ""}
            </span>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="batch-kpi-grid">
        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#eff6ff", color: "#2563eb" }}>
            <FiBookOpen size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Batch Fee / Student</span>
            <span className="batch-kpi-value">₹{kpis.totalFeesPerStudent}</span>
            <span className="batch-kpi-subtext">Per enrolled candidate</span>
          </div>
        </div>

        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#f0fdf4", color: "#16a34a" }}>
            <FiUsers size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Total Enrolled</span>
            <span className="batch-kpi-value">{kpis.totalStudents}</span>
            <span className="batch-kpi-subtext">Active student subscriptions</span>
          </div>
        </div>

        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#ecfdf5", color: "#059669" }}>
            <FiDollarSign size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Total Collected</span>
            <span className="batch-kpi-value" style={{ color: "#059669" }}>
              ₹{kpis.totalCollected}
            </span>
            <span className="batch-kpi-subtext">Confirmed payments</span>
          </div>
        </div>

        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#fffbeb", color: "#d97706" }}>
            <FiClock size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Due / Pending Balance</span>
            <span className="batch-kpi-value" style={{ color: "#d97706" }}>
              ₹{kpis.totalDue}
            </span>
            <span className="batch-kpi-subtext">Awaiting clearance</span>
          </div>
        </div>

        <div className="batch-kpi-card">
          <div className="batch-kpi-icon-wrap" style={{ background: "#faf5ff", color: "#9333ea" }}>
            <FiFileText size={22} />
          </div>
          <div className="batch-kpi-content">
            <span className="batch-kpi-label">Assigned Tests</span>
            <span className="batch-kpi-value">
              {kpis.publishedTestsCount} / {kpis.totalTests}
            </span>
            <span className="batch-kpi-subtext">Published vs total tests</span>
          </div>
        </div>
      </div>

      {/* Tabs navigation */}
      <div className="batch-tabs-bar">
        <button
          className={`batch-tab-btn ${activeTab === "tests" ? "active" : ""}`}
          onClick={() => setActiveTab("tests")}
        >
          <FiFileText size={16} />
          Assigned Tests
          <span className="batch-tab-badge">{tests.length}</span>
        </button>

        <button
          className={`batch-tab-btn ${activeTab === "students" ? "active" : ""}`}
          onClick={() => setActiveTab("students")}
        >
          <FiUsers size={16} />
          Students & Fee Payments
          <span className="batch-tab-badge">{subscriptions.length}</span>
        </button>
      </div>

      {/* Tab 1: Assigned Tests Section */}
      {activeTab === "tests" && (
        <div className="batch-tab-content-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
            <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b", fontWeight: 600 }}>
              Tests Assigned to this Batch
            </h3>
            <div style={{ position: "relative", minWidth: "260px" }}>
              <input
                type="text"
                placeholder="Search tests..."
                value={testSearch}
                onChange={(e) => setTestSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 34px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  outline: "none",
                }}
              />
              <FiSearch
                size={16}
                style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
              />
            </div>
          </div>

          {loading ? (
            <TableSkeleton
              columns={[
                { label: "Test No" },
                { label: "Topic" },
                { label: "Questions" },
                { label: "Duration" },
                { label: "Status" },
                { label: "Materials" },
              ]}
              rows={4}
            />
          ) : filteredTests.length > 0 ? (
            <div className="batch-table-container">
              <table className="batch-custom-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Test No</th>
                    <th>Topic / Syllabus</th>
                    <th>Questions</th>
                    <th>Duration</th>
                    <th>Schedule / Status</th>
                    <th>Materials / Links</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTests.map((test, idx) => (
                    <tr key={test._id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <strong>Test {test.testNo || idx + 1}</strong>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500, color: "#1e293b" }}>
                          {test.subTopic || test.headlineTagline || "General Test"}
                        </div>
                        {test.syllabus && (
                          <div style={{ fontSize: "12px", color: "#64748b" }}>{test.syllabus}</div>
                        )}
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, color: "#0284c7" }}>
                          {test.totalQuestions || 0} Qs
                        </span>
                      </td>
                      <td>
                        {test.examduration ? `${test.examduration} mins` : "Not specified"}
                      </td>
                      <td>
                        <span
                          className={`badge-status ${
                            test.isPublished ? "published" : "draft"
                          }`}
                        >
                          {test.isPublished ? "● Published" : "● Draft / Scheduled"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          {test.questionFiles?.questionPdf && (
                            <a
                              href={test.questionFiles.questionPdf}
                              target="_blank"
                              rel="noreferrer"
                              className="batch-action-link-btn"
                              title="View Question Paper"
                            >
                              <FiFileText size={12} /> Paper
                            </a>
                          )}
                          {test.questionFiles?.videoLink && (
                            <a
                              href={test.questionFiles.videoLink}
                              target="_blank"
                              rel="noreferrer"
                              className="batch-action-link-btn"
                              title="Watch Video Solution"
                            >
                              <FiExternalLink size={12} /> Video
                            </a>
                          )}
                          {test.questionFiles?.omrsheet && (
                            <a
                              href={test.questionFiles.omrsheet}
                              target="_blank"
                              rel="noreferrer"
                              className="batch-action-link-btn"
                              title="OMR Sheet"
                            >
                              OMR
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="batch-empty-state">
              <FiFileText className="batch-empty-icon" />
              <h4>No Tests Found</h4>
              <p>No tests have been created or assigned under this batch yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Students & Payments Section */}
      {activeTab === "students" && (
        <div className="batch-tab-content-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
            <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b", fontWeight: 600 }}>
              Enrolled Students & Fee Collections
            </h3>
            <div style={{ position: "relative", minWidth: "260px" }}>
              <input
                type="text"
                placeholder="Search student or phone..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 34px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  outline: "none",
                }}
              />
              <FiSearch
                size={16}
                style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
              />
            </div>
          </div>

          {loading ? (
            <TableSkeleton
              columns={[
                { label: "Student Name" },
                { label: "Contact Info" },
                { label: "Total Fee" },
                { label: "Paid" },
                { label: "Due Balance" },
                { label: "Payment Status" },
                { label: "Actions" },
              ]}
              rows={4}
            />
          ) : filteredStudents.length > 0 ? (
            <div className="batch-table-container">
              <table className="batch-custom-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Student Name</th>
                    <th>Contact Info</th>
                    <th>Total Fee</th>
                    <th>Paid Amount</th>
                    <th>Due Balance</th>
                    <th>Payment Status</th>
                    <th>Method / Trx ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((std, idx) => (
                    <tr key={std.id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <span
                          style={{
                            color: "#2563eb",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            if (std.studentId) {
                              navigate(
                                `/students/profile/student/${std.studentId}/institute/${std.instituteId}/batchId/${std.batchId}`
                              );
                            }
                          }}
                        >
                          {std.name}
                        </span>
                        {std.city && (
                          <div style={{ fontSize: "11px", color: "#64748b" }}>{std.city}</div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{std.phone}</div>
                        <div style={{ fontSize: "11px", color: "#94a3b8" }}>{std.enrolledDate}</div>
                      </td>
                      <td>
                        <strong>₹{std.totalFee}</strong>
                      </td>
                      <td>
                        <strong style={{ color: "#16a34a" }}>₹{std.paidAmount}</strong>
                      </td>
                      <td>
                        <strong style={{ color: std.dueBalance > 0 ? "#dc2626" : "#16a34a" }}>
                          ₹{std.dueBalance}
                        </strong>
                      </td>
                      <td>
                        <span
                          className={`badge-status ${
                            std.paymentStatus === "paid"
                              ? "paid"
                              : std.paymentStatus === "failed"
                              ? "failed"
                              : "pending"
                          }`}
                        >
                          ● {std.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: "12px", fontWeight: 500 }}>{std.paymentMethod}</div>
                        <div style={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace" }}>
                          {std.transactionId !== "N/A" ? std.transactionId : ""}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "8px" }}>
                          {std.receiptUrl && (
                            <button
                              className="batch-action-receipt-btn"
                              onClick={() => setSelectedReceipt(std.receiptUrl)}
                              title="View Payment Receipt / Verification Proof"
                            >
                              <FiEye size={12} /> Receipt
                            </button>
                          )}
                          {std.studentId && (
                            <button
                              className="batch-action-link-btn"
                              onClick={() =>
                                navigate(
                                  `/students/profile/student/${std.studentId}/institute/${std.instituteId}/batchId/${std.batchId}`
                                )
                              }
                            >
                              Profile
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="batch-empty-state">
              <FiUsers className="batch-empty-icon" />
              <h4>No Students Enrolled</h4>
              <p>No candidates have registered or subscribed for this batch yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Payment Receipt Modal */}
      {selectedReceipt && (
        <div
          className="batch-receipt-modal-backdrop"
          onClick={() => setSelectedReceipt(null)}
        >
          <div
            className="batch-receipt-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="batch-receipt-modal-header">
              <h3>Payment Proof / Receipt</h3>
              <button onClick={() => setSelectedReceipt(null)}>✕</button>
            </div>
            <div className="batch-receipt-modal-body">
              <img
                src={selectedReceipt}
                alt="Receipt"
                className="batch-receipt-img"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchDetails;
